import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import DOMPurify from 'dompurify';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import LazyImage from '../common/LazyImage';
import { useHeader } from '../../contexts/HeaderContext';
import { motion } from 'framer-motion';

interface CourseGroup {
    groupName: string;
    groupNameEn?: string;
    courses: any[]; // 这里简化处理，实际使用时应该是 EntityReference[]
    singleSelection: boolean;
    required: boolean;
}

interface Program {
    id: string;
    title: string;
    titleEn?: string;
    subtitle?: string;
    subtitleEn?: string;
    content: string;
    contentEn?: string;
    categories: string[];
    status: string;
    publishDate: Timestamp;
    cover?: string;
    showOnHome?: boolean;
    courseGroups?: CourseGroup[];
}

interface Course {
    id: string;
    name: string;
    nameEn?: string;
    avatar?: string;
    courseContent?: string;
    scheduleInfo?: string;
    status: string;
    price: number;
    location?: string;
    groupId?: string; // 用于标识课程属于哪个组
}

const ProgramDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [program, setProgram] = useState<Program | null>(null);
    const [courseGroups, setCourseGroups] = useState<{[key: string]: Course[]}>({});
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { setHeaderTheme } = useHeader();
    const [showLightbox, setShowLightbox] = useState(false);

    // 判断文章类型，返回对应的列表页面
    const getReturnPath = (categories: string[]) => {
        // 项目类型的分类
        const programCategories = ['saturday', 'afterSchool', 'summerCamp', 'satAct', 'volunteer'];
        // 如果有任何一个分类是项目分类，就返回项目列表页面
        const hasProgram = categories?.some(cat => programCategories.includes(cat));
        return hasProgram ? '/programs' : '/news';
    };

    useEffect(() => {
        const fetchProgram = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'programs', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setProgram({
                        id: docSnap.id,
                        ...data,
                    } as Program);

                    // 获取关联的课程组
                    const groups: {[key: string]: Course[]} = {};
                    
                    // 处理新的课程组结构
                    if (data.courseGroups && Array.isArray(data.courseGroups)) {
                        // 遍历每个课程组
                        for (let i = 0; i < data.courseGroups.length; i++) {
                            const group = data.courseGroups[i];
                            const groupId = `group-${i}`;
                            
                            // 如果组中有课程
                            if (group.courses && Array.isArray(group.courses)) {
                                const coursesPromises = group.courses.map((courseRef: any) => {
                                    // 检查是否为引用类型
                                    if (courseRef.path) {
                                        return getDoc(courseRef);
                                    }
                                    // 如果只是存储了 ID
                                    return getDoc(doc(db, 'courses', courseRef));
                                });

                                const courseDocs = await Promise.all(coursesPromises);
                                const coursesData: Course[] = [];

                                for (const doc of courseDocs) {
                                    if (doc.exists()) {
                                        const courseData = doc.data() as any;
                                        coursesData.push({
                                            id: doc.id,
                                            name: courseData.name || '',
                                            nameEn: courseData.nameEn || '',
                                            avatar: courseData.avatar,
                                            courseContent: courseData.courseContent || '',
                                            scheduleInfo: courseData.scheduleInfo || '',
                                            status: courseData.status || 'pending',
                                            price: courseData.price || 0,
                                            location: courseData.location || '',
                                            groupId: groupId
                                        });
                                    }
                                }
                                
                                // 将此组的课程添加到组映射中
                                groups[groupId] = coursesData;
                            }
                        }
                    } 
                    // 兼容旧版数据结构
                    else if (data.courses && Array.isArray(data.courses)) {
                        const coursesPromises = data.courses.map((courseRef: any) => {
                            // 检查是否为引用类型
                            if (courseRef.path) {
                                return getDoc(courseRef);
                            }
                            // 如果只是存储了 ID
                            return getDoc(doc(db, 'courses', courseRef));
                        });

                        const courseDocs = await Promise.all(coursesPromises);
                        const coursesData: Course[] = [];

                        for (const doc of courseDocs) {
                            if (doc.exists()) {
                                const courseData = doc.data() as any;
                                coursesData.push({
                                    id: doc.id,
                                    name: courseData.name || '',
                                    nameEn: courseData.nameEn || '',
                                    avatar: courseData.avatar,
                                    courseContent: courseData.courseContent || '',
                                    scheduleInfo: courseData.scheduleInfo || '',
                                    status: courseData.status || 'pending',
                                    price: courseData.price || 0,
                                    location: courseData.location || '',
                                    groupId: 'default'
                                });
                            }
                        }
                        
                        // 将所有课程作为默认组
                        groups['default'] = coursesData;
                    } else {
                        // 如果没有课程组或课程，则设置为空对象
                        setCourseGroups({});
                    }
                    
                    setCourseGroups(groups);
                } else {
                    console.log('No such document!');
                }
            } catch (error) {
                console.error('Error fetching program:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgram();
    }, [id]);

    // 修改 HTML 清理和处理函数
    const createCleanHtml = (content: string) => {
        // 创建一个临时的 div 来解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(content, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['style', 'class', 'align', 'src', 'width', 'height']
        });

        // 处理表格，添加响应式表格类和包装容器
        const tables = tempDiv.getElementsByTagName('table');
        Array.from(tables).forEach(table => {
            // 给表格添加响应式类
            table.classList.add('responsive-table');
            
            // 确保所有表格单元格有适当的类名
            const cells = table.querySelectorAll('th, td');
            cells.forEach(cell => {
                cell.classList.add('table-cell');
                // 如果单元格没有宽度属性，添加默认样式
                if (!cell.hasAttribute('width')) {
                    cell.classList.add('break-words');
                }
            });
            
            // 将表格包装在一个容器中以实现水平滚动
            const wrapperDiv = document.createElement('div');
            wrapperDiv.className = 'table-responsive';
            // 将表格从原来的位置移除
            table.parentNode?.insertBefore(wrapperDiv, table);
            // 将表格添加到包装容器中
            wrapperDiv.appendChild(table);
        });

        // 替换所有 img 标签为自定义组件的占位符
        const imgs = tempDiv.getElementsByTagName('img');
        Array.from(imgs).forEach((img, index) => {
            const src = img.getAttribute('src') || '';
            const alt = img.getAttribute('alt') || '';
            const className = img.getAttribute('class') || '';

            // 创建一个占位符标签，稍后会被替换为 LazyImage 组件
            const placeholder = document.createElement('img');
            placeholder.setAttribute('data-lazy-src', src);
            placeholder.setAttribute('data-lazy-alt', alt);
            placeholder.setAttribute('data-lazy-class', className);
            placeholder.setAttribute('data-lazy-index', index.toString());
            img.parentNode?.replaceChild(placeholder, img);
        });

        return tempDiv.innerHTML;
    };
    // 处理报名按钮点击
    const handleEnrollClick = () => {
        // 检查用户是否已登录
        if (!auth.currentUser) {
            // 保存用户想要报名的项目信息，以便登录后重定向
            sessionStorage.setItem('enrollmentRedirect', JSON.stringify({
                programId: id
            }));

            // 提示用户需要登录
            alert(t('program.loginRequired'));

            // 重定向到登录页面
            navigate('/login', { state: { from: `/program/${id}` } });
            return;
        }
        // 检查用户是否已验证邮箱
        if (!auth.currentUser?.emailVerified) {
            // 提示用户需要验证邮箱
            alert(t('emailVerification.subtitle'));
            navigate('/verify-email');
            return;
        }

        // 用户已登录，跳转到报名页面
        navigate(`/registration?program=${id}`);
    };

    // 渲染处理后的内容
    const renderContent = () => {
        if (!program) return null;
        const content = i18n.language === 'zh' ? program.content : program.contentEn;
        const cleanHtml = createCleanHtml(content);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;

        // 查找所有需要替换为 LazyImage 的占位符
        const placeholders = tempDiv.querySelectorAll('img[data-lazy-src]');
        placeholders.forEach(placeholder => {
            const src = placeholder.getAttribute('data-lazy-src') || '';
            const alt = placeholder.getAttribute('data-lazy-alt') || '';
            const className = placeholder.getAttribute('data-lazy-class') || '';

            // 创建 LazyImage 组件的包装元素
            const wrapper = document.createElement('div');
            wrapper.className = 'my-4';
            wrapper.innerHTML = `<div class="lazy-image" data-src="${src}" data-alt="${alt}" data-class="${className}"></div>`;
            placeholder.parentNode?.replaceChild(wrapper, placeholder);
        });

        return (
            <div
                className="article-content prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }}
                ref={el => {
                    if (el) {
                        // 找到所有需要替换的占位符
                        const lazyImages = el.getElementsByClassName('lazy-image');
                        Array.from(lazyImages).forEach(placeholder => {
                            const src = placeholder.getAttribute('data-src') || '';
                            const alt = placeholder.getAttribute('data-alt') || '';
                            const className = placeholder.getAttribute('data-class') || '';

                            // 创建 LazyImage 组件的包装元素
                            const lazyImageContainer = document.createElement('div');
                            const root = createRoot(lazyImageContainer);
                            root.render(<LazyImage src={src} alt={alt} className={className} />);
                            placeholder.parentNode?.replaceChild(lazyImageContainer, placeholder);
                        });

                        // 为所有表格添加额外的事件处理（如果需要）
                        const tables = el.getElementsByClassName('responsive-table');
                        Array.from(tables).forEach(table => {
                            // 可以在这里添加额外的表格功能（如果需要）
                        });
                    }
                }}
            />
        );
    };

    // 渲染课程列表
    const renderCourses = () => {
        const groupIds = Object.keys(courseGroups);
        
        if (groupIds.length === 0) {
            return (
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">{t('program.noCourses')}</p>
                </div>
            );
        }

        return (
            <div className="mt-8">
                {/* <h3 className="text-xl font-bold mb-4">{program.categories?.includes('summerCamp') ? t('program.availableCamps') : t('program.availableCourses')}</h3> */}
                
                {groupIds.map((groupId) => {
                    const courses = courseGroups[groupId];
                    const groupInfo = program?.courseGroups?.find((g, index) => `group-${index}` === groupId);
                    
                    return (
                        <div key={groupId} className="mb-8">
                            {/* 显示组标题和描述（如果有） */}
                            {groupInfo && (
                                <div className="mb-4">
                                    <h4 className="text-lg font-semibold text-gray-800">{i18n.language === 'zh' ? groupInfo.groupName : groupInfo.groupNameEn}</h4>
                                    {groupInfo.singleSelection && (
                                        <div className="mt-1 text-sm text-blue-600 flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                            </svg>
                                            <span>{t('registration.singleCourseAvailable')}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {courses.map(course => (
                                    <div key={course.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        {course.avatar && (
                                            <div className="h-48 overflow-hidden">
                                                <img
                                                    src={course.avatar}
                                                    alt={course.name}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                        )}
                                        <div className="p-4">
                                            <h4 className="font-bold text-lg mb-2">{i18n.language === 'zh' ? course.name : course.nameEn}</h4>
                                            {course.scheduleInfo && (
                                                <div className="mb-3 h-[80px]">
                                                    <span className="text-sm font-medium">{t('course.duration')}:</span>
                                                    <p className="text-sm text-gray-600 mt-1">{course.scheduleInfo}</p>
                                                </div>
                                            )}
                                            {course.location && (
                                                <div className="mb-3 h-[60px]">
                                                    <span className="text-sm font-medium">{t('profile.location')}:</span>
                                                    <p className="text-sm text-gray-600 mt-1">{course.location}</p>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center mt-2">
                                                <span className="text-blue-700 font-medium">${course.price}</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    course.status === 'pending' 
                                                        ? 'bg-blue-100 text-blue-800' 
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {course.status === 'pending' ? t('course.enrollmentOpen') : t('course.enrollmentClosed')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-16">
                <div className="animate-pulse">
                    <div className="h-64 bg-gray-200 rounded-none mb-6"></div>
                    <div className="container mx-auto px-4">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!program) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">{t('program.notFound')}</h2>
                    <p className="mb-4">{t('program.notFoundDesc')}</p>
                    <Link
                        to="/programs"
                        className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors"
                    >
                        {t('program.backToList')}
                    </Link>
                </div>
            </div>
        );
    }

    const returnPath = getReturnPath(program.categories || []);

    return (
        <div className="min-h-screen mt-24 pb-20 relative">
            {/* 简化后的Hero区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 relative mt-16">
                <div className="container mx-auto px-4">
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center"
                    >
                        <Link
                            to={returnPath}
                            className="inline-flex items-center text-blue-100 mb-4 hover:text-white transition-colors absolute left-4 sm:left-8"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            {t('program.backToList')}
                        </Link>
                        
                        <h1 className="text-4xl font-bold text-white mb-4 pt-16">{i18n.language === 'zh' ? program.title : (program.subtitle || program.title)}</h1>
                        
                        {program.subtitle && (
                            <p className="text-xl text-blue-100 max-w-3xl mx-auto px-4 mb-4">
                                {i18n.language === 'zh' ? program.subtitle : (program.title || program.subtitle)}
                            </p>
                        )}
                        
                        <div className="flex flex-wrap items-center justify-center text-sm text-blue-100 gap-4 mt-4">
                            <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                </svg>
                                {program.publishDate.toDate().toLocaleDateString()}
                            </span>
                            <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                                </svg>
                                {program.categories?.map(cat => t(`program.categories.${cat}`)).join(', ') || ''}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${program.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                {t(`program.statuses.${program.status}`)}
                            </span>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* 主要内容区域 */}
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {renderContent()}
                    </motion.div>
                </div>

                {/* 课程列表 */}
                {/* <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    {renderCourses()}
                </motion.div> */}
            </div>

            {/* 图片灯箱 - 如果需要展示封面图 */}
            {program.cover && (
                <Lightbox
                    open={showLightbox}
                    close={() => setShowLightbox(false)}
                    slides={[{ src: program.cover }]}
                />
            )}

            {/* 固定在底部的报名按钮 - 使用fixed而不是sticky */}
            {program.status === 'active' && (
                <div className="fixed bottom-0 left-0 right-0 z-40 py-4 px-4 bg-white bg-opacity-95 shadow-lg border-t border-gray-200 backdrop-blur-sm">
                    <div className="container mx-auto flex justify-between items-center">
                        <div className="flex-grow">
                            <h3 className="text-lg font-bold text-gray-800">{program.title}</h3>
                            <p className="text-sm text-gray-600">{t('course.enrollmentOpen')}</p>
                        </div>
                        <button
                            onClick={handleEnrollClick}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg text-lg font-bold transition-colors duration-300 shadow-md hover:shadow-lg flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            {t('course.enroll')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramDetail; 